import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface DistributionConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  method: 'pdf' | 'mobile' | 'delete' | 'reassign';
  operatorName?: string; // undefined = all operators
  taskCount?: number;
  includeBriefing?: boolean;
  briefingAvailable?: boolean;
  reassignFromOperatorName?: string;
  reassignToOperatorName?: string;
  onConfirm: () => void | Promise<void>;
}

export function DistributionConfirmModal({
  open,
  onOpenChange,
  method,
  operatorName,
  taskCount,
  includeBriefing = false,
  briefingAvailable = false,
  reassignFromOperatorName,
  reassignToOperatorName,
  onConfirm
}: DistributionConfirmModalProps) {
  const [isConfirming, setIsConfirming] = useState(false);

  const isDelete = method === 'delete';
  const isReassign = method === 'reassign';
  const methodLabel = method === 'pdf' ? 'PDF' : method === 'delete' ? 'Delete' : 'Mobile';
  const operatorText = operatorName 
    ? `for ${operatorName}` 
    : 'for all operators';
  const taskText = taskCount ? ` (${taskCount} tasks)` : '';

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isDelete
              ? '🗑️ Delete All Assignments?'
              : isReassign
                ? '↔️ Reassign Store?'
              : method === 'mobile'
                ? '📱 Send to Mobile?'
                : 'Mark Assignments as Sent?'}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              {isReassign ? (
                <>
                  <p>
                    Move {reassignFromOperatorName ?? 'this operator'}&apos;s assignments at{' '}
                    {operatorName ?? 'this store'}
                    {taskText} to {reassignToOperatorName ?? 'the selected operator'}?
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-900 text-sm space-y-2">
                    <p className="font-semibold">
                      This cycle&apos;s formats at this store will be assigned to{' '}
                      {reassignToOperatorName ?? 'the new operator'} from today on, including future cycles.
                    </p>
                    <p>To hand the store back later, reassign it again.</p>
                    <ul className="list-disc list-inside">
                      <li>It comes off the current operator&apos;s task list on their next sync</li>
                      <li>It is sent automatically to the new operator, ready for their next sync</li>
                      <li>Removal tasks owned by a different operator at this store are not affected</li>
                    </ul>
                    <p>Ask the current operator to sync any saved work before you reassign.</p>
                  </div>
                </>
              ) : isDelete ? (
                <>
                  <p>
                    Delete all assignments and submissions {operatorText}{taskText}?
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-md p-3 text-red-800">
                    <p className="font-semibold text-sm">⚠️ This will permanently delete:</p>
                    <ul className="text-sm mt-1 list-disc list-inside">
                      <li>All mobile assignments</li>
                      <li>All store visit progress</li>
                      <li>All posted submissions and evidence photos</li>
                    </ul>
                    <p className="text-sm mt-2 font-semibold">This action cannot be undone.</p>
                  </div>
                </>
              ) : method === 'mobile' ? (
                <>
                  <p>
                    Send all task assignments {operatorText} to mobile{taskText}?
                  </p>
                  <p className="text-sm text-muted-foreground">
                    This will make tasks available on the operator's mobile device.
                  </p>
                  <div
                    className={`rounded-md border p-3 text-sm ${
                      briefingAvailable && includeBriefing
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-slate-200 bg-slate-50 text-slate-700'
                    }`}
                  >
                    {briefingAvailable
                      ? includeBriefing
                        ? 'Operator briefing will be included. Operators must acknowledge it before starting stores.'
                        : 'Operator briefing will NOT be included. Tick "Include briefing" before sending if needed.'
                      : 'No saved cycle briefing is available to include yet.'}
                  </div>
                </>
              ) : (
                <>
                  <p>
                    Mark all task assignments {operatorText} as sent ({methodLabel}){taskText}?
                  </p>
                  <p className="text-sm text-muted-foreground">
                    This will update the distribution status.
                  </p>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isConfirming}>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirm} 
            disabled={isConfirming}
            className={isDelete ? 'bg-red-600 hover:bg-red-700' : isReassign ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            {isConfirming 
              ? 'Processing...' 
              : isDelete
                ? 'Delete All'
                : isReassign
                  ? 'Reassign Store'
                : 'Confirm'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
